import { Package, Fence, ShieldCheck } from "lucide-react";
import { getCostCategories, getCostTrades } from "@shared/schema";
import { COST_NAV_SECTIONS, type CostNavItem, type CostNavSection } from "./cost-library-nav";
import type { CostTaskNavInput } from "./cost-library-labels";
import { matchesCostNavItem } from "./cost-library-membership";

/** Ownership of catalogue classifications in the existing visual navigation.
 * This is browsing metadata, not pricing or AI work-item classification.
 */
const CATEGORY_SECTIONS: Record<string, string> = {
  preliminaries: "preparation", scaffolding: "preparation",
  demolition: "demolition_removal", waste: "demolition_removal",
  groundworks: "drainage", foundations: "foundation", damp_proofing: "below_dpc",
  brickwork: "walls", internal_walls: "walls",
  carpentry: "carpentry_section", staircases: "carpentry_section",
  steel: "structural_elements", steel_medium: "structural_elements", steel_heavy: "structural_elements",
  roofing: "roof", windows_doors: "openings", electrics: "electrics", plumbing: "plumbing",
  heating: "heating", ventilation: "heating", gas_works: "heating",
  tiling: "internal_finishes", plastering: "internal_finishes", decorating: "internal_finishes", flooring: "internal_finishes",
  cladding: "exterior_finishes",
  kitchens: "fixtures_installations", bathrooms: "fixtures_installations", making_good: "fixtures_installations",
  other: "other_section",
};

const BROWSE_ALIASES: Record<string, NonNullable<CostNavItem["browseFilters"]>> = {
  external_cladding: [{ categoryFilter: "cladding", tradeFilter: "external_cladding" }],
  rainwater_goods: [{ categoryFilter: "roofing", tradeFilter: "rainwater" }],
  drylining: [
    { categoryFilter: "internal_walls", tradeFilter: "drylining" },
    { categoryFilter: "internal_walls", tradeFilter: "plasterboard" },
  ],
  stairs: [{ categoryFilter: "staircases" }],
  steel_beams: [
    { categoryFilter: "steel_medium", tradeFilter: "steelwork" },
    { categoryFilter: "steel_heavy", tradeFilter: "steelwork" },
  ],
  heating_general: [{ categoryFilter: "heating", tradeFilter: "general" }],
  waterproofing: [{ categoryFilter: "damp_proofing", tradeFilter: "waterproofing" }],
};

function readable(value: string, emptyLabel: string) {
  return value.trim().replace(/[_-]+/g, " ").replace(/\b\w/g, letter => letter.toUpperCase()) || emptyLabel;
}

export function catalogueCategoryId(category: string) {
  return `catalogue-category:${encodeURIComponent(category)}`;
}

export function catalogueTradeId(category: string, trade: string) {
  return `catalogue-trade:${encodeURIComponent(category)}:${encodeURIComponent(trade)}`;
}

/** Complete navigation from the SAME effective catalogue used by every screen.
 * Existing named leaves and their IDs stay stable. Uncovered category/trade
 * pairs become exact, data-derived leaves, rather than "uncategorised" tasks.
 * New catalogue classifications therefore do not need per-item menu patches.
 */
export function buildCostLibraryNavigation(tasks: readonly CostTaskNavInput[], market?: string | null): CostNavSection[] {
  const sections = COST_NAV_SECTIONS.map(section => ({
    ...section,
    items: section.items.map(item => ({
      ...item,
      ...(BROWSE_ALIASES[item.id] ? { browseFilters: BROWSE_ALIASES[item.id] } : {}),
    })),
  })) as CostNavSection[];
  const categories = getCostCategories(market);
  const trades = getCostTrades(market);
  const ordered = [...tasks].sort((a, b) => a.category.localeCompare(b.category) || a.trade.localeCompare(b.trade));
  for (const task of ordered) {
    if (sections.some(section => section.items.some(item => matchesCostNavItem(task, item)))) continue;
    const categoryLabel = categories.find(category => category.value === task.category)?.label
      ?? readable(task.category, "Unassigned category");
    const sectionId = CATEGORY_SECTIONS[task.category] ?? catalogueCategoryId(task.category);
    let section = sections.find(section => section.id === sectionId);
    if (!section) {
      section = {
        id: sectionId, label: categoryLabel, generated: true,
        icon: task.category === "external_works" ? Fence : task.category === "fire_protection" ? ShieldCheck : Package,
        items: [],
      };
      sections.splice(Math.max(0, sections.findIndex(section => section.id === "other_section")), 0, section);
    }
    const id = catalogueTradeId(task.category, task.trade);
    if (!section.items.some(item => item.id === id)) {
      section.items.push({
        id,
        label: trades[task.category]?.trades.find(trade => trade.value === task.trade)?.label
          ?? (task.trade === "general" ? categoryLabel : readable(task.trade, "Unassigned trade")),
        icon: section.icon, categoryFilter: task.category, tradeFilter: task.trade, generated: true,
      });
    }
  }
  return sections;
}
